/**
 * Frontend access-control composable.
 *
 * Mirrors config/access.php, which is the backend source of truth for
 * these role-ID gates — keep the two files in sync.
 *
 * THE MODEL: three tiers only — platform admin (4, 6), manager (5),
 * normal user (2). Any other role id behaves as a normal user: these are
 * allowlists, so unknown values fail closed. The router guard re-syncs
 * the localStorage-cached role from check-session on every navigation
 * (see plugins/1.router/guards.js), so a DB-side role change demotes the
 * UI without waiting for re-login.
 */

export const ADMIN_ROLE_IDS = [4, 6]
export const MANAGER_ROLE_IDS = [5]
export const CONTROL_CENTER_ROLE_IDS = [4, 5, 6]

// Read-only platform tier (2026-09-02): rb_support (6) sees every admin
// surface but the API refuses all its writes (App\Support\ReadOnlyAdmin,
// enforced inside the management gate middleware). This mirror only hides
// write affordances in the UI.
export const READ_ONLY_ADMIN_ROLE_IDS = [6]

// Equipment (DCG) management: RightBytes Admin and Support only.
// Customer roles retain their existing generic equipment list access.
// Backend mirror: config('access.dcg_equipment_role_ids') via the
// dcg.equipment middleware — this only drives nav/routing.
export const DCG_EQUIPMENT_ROLE_IDS = [4, 6]

// Org-level scope (2026-09-04): only RB staff (4/6) and store managers (5)
// may scope the navbar picker to a PARENT ORGANIZATION. Everyone else —
// staff, brand managers (108), and every other normal-user role — picks
// stores only: an org scope has no RightBytes_Store row, which breaks the
// StoreNumber-keyed pages (Equipment/DCG, hardware, waste). Allowlist, so
// unknown roles fail closed to store-only.
export const ORG_SCOPE_ROLE_IDS = [4, 5, 6]

/**
 * Reads the current user's role ID from localStorage.
 * Same parsing pattern used across the admin/control-center composables.
 *
 * @returns {number|null} The parsed role ID (may be non-finite if the
 *   stored data is malformed), or `null` if no user data is available.
 */
export function getRoleId() {
  try {
    const raw = localStorage.getItem('userData')
    if (!raw || raw === 'undefined') return null

    const user = JSON.parse(raw)

    return Number(user?.role?.id ?? user?.role_id ?? null)
  } catch {
    return null
  }
}

/**
 * @param {number|null} [roleId] Defaults to the current user's role ID.
 * @returns {boolean}
 */
export function isAdminRole(roleId = getRoleId()) {
  return Number.isFinite(roleId) && ADMIN_ROLE_IDS.includes(roleId)
}

/**
 * @param {number|null} [roleId] Defaults to the current user's role ID.
 * @returns {boolean}
 */
export function hasControlCenterRole(roleId = getRoleId()) {
  return Number.isFinite(roleId) && CONTROL_CENTER_ROLE_IDS.includes(roleId)
}

/**
 * True for the store-manager tier (role 5): the people who buy modules for
 * their own locations (2026-09-18). Platform admins are not managers.
 *
 * @param {number|null} [roleId] Defaults to the current user's role ID.
 * @returns {boolean}
 */
export function isManagerRole(roleId = getRoleId()) {
  return Number.isFinite(roleId) && MANAGER_ROLE_IDS.includes(roleId)
}

/**
 * True for the read-only admin tier (rb_support): full view of the admin
 * surfaces, no writes. Use it to hide buttons/menus that would only ever
 * 403 — the API enforces regardless.
 *
 * @param {number|null} [roleId] Defaults to the current user's role ID.
 * @returns {boolean}
 */
export function isReadOnlyAdmin(roleId = getRoleId()) {
  return Number.isFinite(roleId) && READ_ONLY_ADMIN_ROLE_IDS.includes(roleId)
}

/**
 * Write capabilities (2026-10-01) — mirror of config('access.capabilities').
 * A write route tagged with a capability is allowed for the roles listed
 * here; this is how rb_support (6) manages PEOPLE (invites, memberships,
 * module roles, deactivation) while organization / store / entitlement /
 * billing writes stay read-only for it. Keep in sync with config/access.php.
 * Display convenience only — the API enforces regardless.
 */
export const CAPABILITIES = {
  'data_issues.review': [4, 6],
  'people.manage': [4, 5, 6],
  'memberships.manage': [4, 6],
  'users.role': [4],
}

/**
 * Billing (2026-10-10) — mirror of config('stripe.manager_role_ids'). These
 * roles manage every subscription in their tree and may buy org-wide; every
 * other customer role buys for one location and manages what it bought.
 * Display convenience only — the API decides per row (can_manage).
 */
export const BILLING_MANAGER_ROLE_IDS = [4, 5]

/**
 * True when the current role may perform writes under the capability.
 * Unknown capability names allow nobody (fail closed, same as the server).
 *
 * @param {string} capability One of the CAPABILITIES keys.
 * @param {number|null} [roleId] Defaults to the current user's role ID.
 * @returns {boolean}
 */
export function canWrite(capability, roleId = getRoleId()) {
  const roles = CAPABILITIES[capability]

  return Array.isArray(roles) && Number.isFinite(roleId) && roles.includes(roleId)
}

/**
 * Equipment (DCG) page access — RightBytes Admin and Support only.
 *
 * @param {number|null} [roleId] Defaults to the current user's role ID.
 * @returns {boolean}
 */
export function canAccessDcgEquipment(roleId = getRoleId()) {
  return Number.isFinite(roleId) && DCG_EQUIPMENT_ROLE_IDS.includes(roleId)
}

/**
 * May this role scope the navbar picker to a parent ORGANIZATION?
 * Everyone outside the allowlist gets a store-only picker.
 *
 * @param {number|null} [roleId] Defaults to the current user's role ID.
 * @returns {boolean}
 */
export function canSelectOrgScope(roleId = getRoleId()) {
  return Number.isFinite(roleId) && ORG_SCOPE_ROLE_IDS.includes(roleId)
}

/**
 * Feature flag: user management (the Users page + invite endpoints).
 * Admins always have it; managers only when their users row carries
 * can_manage_users (toggled in MySQL, synced into localStorage from
 * check-session by the router guard). Everyone else: never. The flag was
 * retired for one day (2026-10-13, role alone) and restored 2026-10-14.
 * Backend mirror: EnsureInviteAccess middleware — this is display/routing
 * convenience only, the API enforces it regardless.
 *
 * @returns {boolean}
 */
export function canManageUsers() {
  const roleId = getRoleId()

  if (isAdminRole(roleId)) return true
  if (!MANAGER_ROLE_IDS.includes(roleId)) return false

  try {
    const raw = localStorage.getItem('userData')
    if (!raw || raw === 'undefined') return false

    const user = JSON.parse(raw)

    return Boolean(user?.can_manage_users)
  } catch {
    return false
  }
}
